// status-chat-drive-fields.ts — 06k C10: các field `status --json` thêm cho vòng
// lái từ chat. Read-only, best-effort (file hỏng ⇒ bỏ field, không ném).
import { join } from "node:path";
import { CHAT_DELEGATED_LABEL, CHAT_DELEGATED_SOURCE } from "./chat-delegated-attribution.js";
import { isDirectiveProcessed, listInboxDirectiveIds, readDirective } from "./directive-inbox.js";
import { scanJournal } from "./journal-scan-report.js";
import { MISSION_JOURNAL_FILE_NAME } from "./mission-store.js";
import { type LastRunFile, readLastRun } from "./run-last-outcome-file.js";
import { readSupervisorLiveness, type SupervisorLiveness } from "./status-supervisor-liveness.js";

export interface GateApprovalRow {
  gate: string;
  request_id: string;
  source: "human" | "chat_delegated";
  by: string;
}

export interface QueuedDirectiveRow {
  directive_id: string;
  kind: string;
  source: string;
}

export interface StatusChatDriveFields extends SupervisorLiveness {
  last_run?: LastRunFile;
  resume_at?: string;
  gate_approvals: GateApprovalRow[];
  queued_directives: QueuedDirectiveRow[];
}

async function readGateApprovals(controlRoot: string): Promise<GateApprovalRow[]> {
  const scan = await scanJournal(join(controlRoot, MISSION_JOURNAL_FILE_NAME)).catch(
    () => undefined,
  );
  return (scan?.records ?? []).flatMap(({ delta }) => {
    if (delta.kind !== "gate_approved") return [];
    const d = delta as {
      gate?: unknown;
      request_id?: unknown;
      actor_id?: unknown;
      source?: unknown;
    };
    const delegated = d.source === CHAT_DELEGATED_SOURCE;
    return [
      {
        gate: String(d.gate),
        request_id: String(d.request_id ?? ""),
        source: delegated ? CHAT_DELEGATED_SOURCE : "human",
        by: delegated ? CHAT_DELEGATED_LABEL : String(d.actor_id ?? ""),
      },
    ];
  });
}

/** Directive trong inbox chưa có `.processed` (B12 — pause + approve cùng xếp hàng). */
async function readQueuedDirectives(controlRoot: string): Promise<QueuedDirectiveRow[]> {
  const rows: Array<QueuedDirectiveRow & { at: string }> = [];
  for (const id of await listInboxDirectiveIds(controlRoot).catch(() => [])) {
    try {
      if (await isDirectiveProcessed(controlRoot, id)) continue;
      const d = await readDirective(controlRoot, id);
      if (d)
        rows.push({
          directive_id: d.directive_id,
          kind: d.kind,
          source: d.source,
          at: d.received_at,
        });
    } catch {
      // directive hỏng: không liệt kê (status không bao giờ ném vì inbox)
    }
  }
  return rows.sort((a, b) => a.at.localeCompare(b.at)).map(({ at: _at, ...row }) => row);
}

export async function readStatusChatDriveFields(input: {
  controlRoot: string;
  machineLockPath: string;
  missionId: string;
  now?: () => Date;
}): Promise<StatusChatDriveFields> {
  const [liveness, lastRun, gateApprovals, queued] = await Promise.all([
    readSupervisorLiveness(input.machineLockPath, input.missionId),
    readLastRun(input.controlRoot),
    readGateApprovals(input.controlRoot),
    readQueuedDirectives(input.controlRoot),
  ]);
  const resumeAt = (lastRun?.result as { resume_at?: unknown } | undefined)?.resume_at;
  const nowMs = (input.now ?? (() => new Date()))().getTime();
  const future = typeof resumeAt === "string" && Date.parse(resumeAt) > nowMs;
  return {
    ...liveness,
    ...(lastRun ? { last_run: lastRun } : {}),
    ...(future ? { resume_at: resumeAt } : {}),
    gate_approvals: gateApprovals,
    queued_directives: queued,
  };
}
